/**
 * Browser-API polyfills for the declared floor (see `browserslist` in
 * package.json: Safari / iOS 15, Chrome / Edge 80, Firefox 115; vingreact#981).
 * vingreact#77.
 *
 * This module holds what we install ourselves. Everything else the floor lacks
 * comes from core-js through src/lib/floorPolyfills.generated.ts; both load via
 * src/polyfills.ts (import that, not this module).
 *
 * `build.target` only downlevels SYNTAX. A dependency calling an API newer than
 * our baseline compiles clean and then throws on a learner's phone. pdfjs-dist
 * (via react-pdf) does exactly that, in three places:
 *
 *   | API                     | Available from | Call site in pdfjs 5.4.296            |
 *   |-------------------------|----------------|---------------------------------------|
 *   | `URL.parse`             | Safari 18      | `getUrlProp`, `createValidAbsoluteUrl` |
 *   | `Promise.withResolvers` | Safari 17.4    | `getDocument`, message handlers        |
 *   | `AbortSignal.any`       | Safari 17.4    | fetch streams, touch manager           |
 *
 * `URL.parse` is what crashed 29 learner consume sessions on iOS 17
 * (`TypeError: URL.parse is not a function`); the other two are the same class
 * of bug one dot-release lower, and are fixed here rather than waiting for the
 * next incident report.
 *
 * Installed by side effect on import. Native implementations are never replaced.
 *
 * NOTE ON SCOPES: a worker is a separate global scope. Importing this on the
 * main thread does NOT reach `pdf.worker.mjs`, which uses `URL.parse` and
 * `Promise.withResolvers` too. That is why `src/lib/pdfWorkerEntry.ts` exists
 * and imports `@/polyfills` (which includes this module) first. `scripts/check-browser-api-baseline.mjs`
 * enforces the per-scope rule against the built bundle.
 */

/** Stamped onto every installed polyfill so a build-output scan can prove this
 *  module shipped in a given chunk. See scripts/check-browser-api-baseline.mjs. */
export const BROWSER_POLYFILLS_MARKER = '__ving_browser_polyfills__';

function stamp<T extends object>(fn: T): T {
  Object.defineProperty(fn, 'polyfilledBy', {
    value: BROWSER_POLYFILLS_MARKER,
    configurable: true,
  });
  return fn;
}

function define(target: object, name: string, value: unknown): void {
  Object.defineProperty(target, name, {
    value,
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

function missing(target: object, name: string): boolean {
  return typeof (target as Record<string, unknown>)[name] !== 'function';
}

/* -------------------------------------------------------------- URL.parse -- */

/**
 * Spec-shaped `URL.parse`: a `URL` for anything `new URL()` accepts, `null` for
 * anything it rejects. Never throws.
 */
export function urlParse(url: string | URL, base?: string | URL): URL | null {
  try {
    return base === undefined ? new URL(url) : new URL(url, base);
  } catch {
    return null;
  }
}

export function installUrlParsePolyfill(): boolean {
  if (!missing(URL, 'parse')) return false;
  define(URL, 'parse', stamp(urlParse));
  return true;
}

/* ------------------------------------------------ Promise.withResolvers -- */

export interface PromiseWithResolvers<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
}

/** Spec-shaped `Promise.withResolvers` — subclass-aware via `this`. */
export function promiseWithResolvers<T>(this: PromiseConstructor): PromiseWithResolvers<T> {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: unknown) => void;
  const Ctor = (typeof this === 'function' ? this : Promise) as PromiseConstructor;
  const promise = new Ctor<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

export function installPromiseWithResolversPolyfill(): boolean {
  if (!missing(Promise, 'withResolvers')) return false;
  define(Promise, 'withResolvers', stamp(promiseWithResolvers));
  return true;
}

/* ------------------------------------------------------ AbortSignal.any -- */

/**
 * Spec-shaped `AbortSignal.any`: a signal that aborts as soon as any input
 * signal does, carrying that signal's reason. Already-aborted inputs abort the
 * result synchronously.
 *
 * Listeners are removed BY HAND once any source aborts: the `signal` option of
 * addEventListener (which would do it for us) is Chrome 90 / Safari 15, and
 * Chrome 80 ignores it, so every source would keep a listener alive.
 *
 * Chrome 80 (the floor) has no `AbortSignal.prototype.reason` and its
 * `abort()` ignores an argument: there the result aborts with `reason`
 * undefined. The abort still propagates; only the reason is lost.
 * `test_981_abortsignal_any_without_reason_pinned` pins that shape.
 */
export function abortSignalAny(signals: Iterable<AbortSignal>): AbortSignal {
  const controller = new AbortController();
  const list = [...signals];

  const aborted = list.find((s) => s.aborted);
  if (aborted) {
    controller.abort(aborted.reason);
    return controller.signal;
  }

  const listeners: Array<[AbortSignal, () => void]> = [];
  const removeAll = () => {
    for (const [source, listener] of listeners) source.removeEventListener('abort', listener);
    listeners.length = 0;
  };
  for (const source of list) {
    const listener = () => {
      removeAll();
      controller.abort(source.reason);
    };
    listeners.push([source, listener]);
    source.addEventListener('abort', listener);
  }
  return controller.signal;
}

export function installAbortSignalAnyPolyfill(): boolean {
  if (!missing(AbortSignal, 'any')) return false;
  define(AbortSignal, 'any', stamp(abortSignalAny));
  return true;
}

/* ------------------------------------------------------------------------ */

/** Install every polyfill the current engine is missing. Returns the names
 *  installed (empty on an up-to-date engine). */
export function installBrowserPolyfills(): string[] {
  const installed: string[] = [];
  if (installUrlParsePolyfill()) installed.push('URL.parse');
  if (installPromiseWithResolversPolyfill()) installed.push('Promise.withResolvers');
  if (installAbortSignalAnyPolyfill()) installed.push('AbortSignal.any');
  return installed;
}

installBrowserPolyfills();
